import { LiveKitProvider } from '@gem/call/CallProviders/LiveKitProvider/LiveKitProvider';
import { CallConnectionQuality } from '@gem/call/CallProviders/callProviders.constants';
import {
  ICallProviderFacade,
  ICallProviderPublisher,
  ProviderUid,
} from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { CallMediaPermissionValue } from '@gem/call/CallStore/Call.types';
import { permissions } from '@gem/call/Removable/tasks/permissions/permissions';
import { CallBlurType } from '@gem/call/constants';
import { callBackground } from '@gem/call/storage/adapters';
import { getDefaultDevices } from '@gem/call/tasks/getDefaultDevices';
import { platformInfo } from '@gem/utils';

import { ICallProviderInitParams, ICallProviderParams } from './CallProviderService.types';
import { ChangeDeviceService } from './ChangeDeviceService/ChangeDeviceService';

export class CallProviderService {
  private changeDeviceService: ChangeDeviceService | null = null;
  private isStartScreenSharing = false;
  private isAudioMuting: boolean;
  private isVideoMuting: boolean;
  audioVideoStreamer: ICallProviderFacade;
  publisher: ICallProviderPublisher;
  canUseBlur: boolean;
  call: Call;

  constructor(params: ICallProviderParams) {
    this.call = params.call;
    this.audioVideoStreamer = new LiveKitProvider({
      blurType: this.call.meStore.blurType,
      providerUrl: params.providerUrl,
    });

    this.publisher = this.audioVideoStreamer.publisher;
    this.canUseBlur = this.audioVideoStreamer.canUseBlur;
  }

  async initCall(params: ICallProviderInitParams): Promise<void> {
    this.changeDeviceService = new ChangeDeviceService(this, this.call);
    const devices = await this.changeDeviceService.init();
    await this.audioVideoStreamer.createClient({
      devices,
      videoQualitySettings: this.call.callLimits.videoQualitySettings,
      handlers: {
        onConnectionQualityChanged: this.onConnectionQualityChanged.bind(this),
        onUserJoined: params.handlers.onUserJoined,
        onUserLeft: params.handlers.onUserLeft,
        onJoin: params.handlers.onJoin,
        onLeave: params.handlers.onLeave,
        onUserMediaChanged: params.handlers.onUserMediaChanged,
        onUserVolumeLevelChanged: params.handlers.onUserVolumeLevelChanged,
        onPublisherSpeakingChanged: this.onPublisherSpeakingChanged.bind(this),
        onUserHandAction: params.handlers.onUserHandAction,
        onLocalAudioTrackEnd: (deviceId?: string) => {
          if (deviceId) {
            this.changeDeviceService?.checkMicrophoneEnded(deviceId);
          } else {
            this.changeDeviceService?.checkChangeDevices();
          }
        },
        onLocalAudioTrackStart: (deviceId?: string) => {
          this.changeDeviceService?.holdMicrophone(deviceId);
        },
      },
    });
  }

  playOpponentsAudio(): void {
    this.audioVideoStreamer.playOpponentsAudio();
  }

  private async getSafariScreenSharingTrack(): Promise<MediaStreamTrack | undefined> {
    const mediaStream = await navigator.mediaDevices.getDisplayMedia();
    if (!mediaStream) {
      return;
    }
    const tracks = mediaStream.getVideoTracks();
    return tracks ? tracks[0] : undefined;
  }

  private async getElectronScreenSharingTrack(
    electronSourceId: string
  ): Promise<MediaStreamTrack | undefined> {
    const stream = await (navigator as Navigator).mediaDevices.getUserMedia({
      audio: false,
      video: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        mandatory: {
          chromeMediaSource: 'desktop',
          chromeMediaSourceId: electronSourceId,
        },
      },
    });
    if (!stream) {
      return;
    }
    const tracks = stream.getVideoTracks();
    return tracks ? tracks[0] : undefined;
  }

  private onPublisherSpeakingChanged(isSpeaking: boolean): void {
    this.call.meStore.setIsSpeaking(isSpeaking);
  }

  private onConnectionQualityChanged(quality: CallConnectionQuality): void {
    this.call.setConnectionQuality(quality);
  }

  checkAudioPermission = async (): Promise<boolean> => {
    const hasAudioPermission = await permissions.hasMicrophonePermission();

    if (hasAudioPermission && this.call.audioPermission === CallMediaPermissionValue.Initial) {
      await this.changeDeviceService?.initAudioDevices();
    }

    this.call.setHasAudioPermission(
      hasAudioPermission ? CallMediaPermissionValue.Accepted : CallMediaPermissionValue.Denied
    );

    return hasAudioPermission;
  };

  unmuteAudio = async (needToPublish: boolean): Promise<void> => {
    const hasAudioPermission = await this.checkAudioPermission();

    if (!hasAudioPermission) {
      return;
    }

    if (!this.isAudioMuting) {
      this.isAudioMuting = true;
      const success = await this.audioVideoStreamer.unmuteAudio(needToPublish);

      if (success) {
        this.call.meStore.setIsAudioMuted(false);
      }
      this.isAudioMuting = false;
    }
  };

  muteAudio = async (): Promise<void> => {
    if (!this.isAudioMuting) {
      this.isAudioMuting = true;
      const success = await this.audioVideoStreamer.muteAudio();
      if (success) {
        this.call.meStore.setIsAudioMuted(true);
      }
      this.isAudioMuting = false;
    }
  };

  checkCameraPermission = async (isInitial?: boolean | null): Promise<boolean> => {
    const hasCameraPermission = await permissions.hasCameraPermission(
      this.call.mediaDevices.currentDeviceIds.videoinput!
    );

    this.call.setHasVideoPermission(
      hasCameraPermission ? CallMediaPermissionValue.Accepted : CallMediaPermissionValue.Denied
    );

    if (isInitial && hasCameraPermission) {
      await this.changeDeviceService?.getDevices();
      const defaultDevices = await getDefaultDevices();
      // in firefox we don't have video device
      this.call.mediaDevices.setCurrentDevicesIds({
        ...this.call.mediaDevices.currentDeviceIds,
        videoinput: defaultDevices.videoinput,
      });
    }
    return hasCameraPermission;
  };

  unmuteVideo = async (needToPublish: boolean): Promise<void> => {
    const hasCameraPermission = await this.checkCameraPermission(
      !this.call.mediaDevices.currentDeviceIds.videoinput
    );
    if (!hasCameraPermission) {
      return;
    }

    if (!this.isVideoMuting) {
      this.isVideoMuting = true;
      const success = await this.audioVideoStreamer.unmuteVideo(needToPublish);
      if (success) {
        this.call.meStore.setIsVideoMuted(false);
      }
      this.isVideoMuting = false;
    }
  };

  muteVideo = async (): Promise<void> => {
    if (!this.isVideoMuting) {
      this.isVideoMuting = true;
      const success = await this.audioVideoStreamer.muteVideo();
      if (success) {
        this.call.meStore.setIsVideoMuted(true);
      }
      this.isVideoMuting = false;
    }
  };

  async unpublish(): Promise<void> {
    await this.audioVideoStreamer.unpublish();
  }

  async startCall(): Promise<void> {
    if (
      !this.call.channelId ||
      !this.call.meStore.audioVideoUid ||
      !this.call.meStore.shareScreenUid ||
      !this.call.meStore.audioVideoToken
    ) {
      return;
    }

    if (this.call.audioPermission === CallMediaPermissionValue.Denied && platformInfo.isMobile) {
      this.call.viewState.setIsWaitingAudioShow(true);
    }
    await this.audioVideoStreamer.join({
      channelId: this.call.channelId,
      uid: this.call.meStore.audioVideoUid,
      screenUid: this.call.meStore.shareScreenUid,
      token: this.call.meStore.audioVideoToken,
    });
  }

  async setSomeUserTypeStream(uid: ProviderUid, type: number): Promise<void> {
    this.audioVideoStreamer.setSomeUserTypeStream(uid, type);
  }

  async startScreenShare(params: {
    electronSourceId?: string;
    onStopSharingScreen: () => void;
  }): Promise<boolean> {
    if (
      this.isStartScreenSharing ||
      !this.call.meStore.shareScreenUid ||
      !this.call.meStore.shareScreenToken ||
      !this.call.channelId
    ) {
      return false;
    }

    try {
      this.isStartScreenSharing = true;
      let track;
      if (platformInfo.isSafari) {
        track = await this.getSafariScreenSharingTrack();
      } else if (params.electronSourceId) {
        track = await this.getElectronScreenSharingTrack(params.electronSourceId);
      }

      const success = await this.audioVideoStreamer.startScreenShare({
        channelId: this.call.channelId,
        token: this.call.meStore.shareScreenToken,
        uid: this.call.meStore.shareScreenUid,
        track,
        needToPublish: true,
        onStopSharingScreen: params.onStopSharingScreen,
      });
      if (success) {
        this.call.meStore.setIsSharedScreen(true);
      }
      this.isStartScreenSharing = false;
      return success;
    } catch (e) {
      this.isStartScreenSharing = false;
      throw new Error(e);
    }
  }

  async stopScreenShare(): Promise<void> {
    await this.audioVideoStreamer.stopScreenShare();
    this.call.meStore.setIsSharedScreen(false);
  }

  async publish(): Promise<void> {
    await this.audioVideoStreamer.publish();
  }

  async leave(): Promise<void> {
    await this.audioVideoStreamer.leave();
  }

  async dispose(): Promise<void> {
    await this.setHandRaised(false);
    await this.audioVideoStreamer.dispose();
    this.changeDeviceService?.dispose();
  }

  playOpponentVideo(videoContainer: HTMLDivElement, opponentUid: ProviderUid): void {
    this.audioVideoStreamer.playOpponentVideo(videoContainer, opponentUid);
  }

  stopOpponentVideo(uid: ProviderUid): void {
    this.audioVideoStreamer.stopOpponentVideo(uid);
  }

  playPublisherVideo(videoContainer: HTMLDivElement): HTMLVideoElement | null {
    return this.audioVideoStreamer.playPublisherVideo(videoContainer);
  }

  stopPublisherVideo(container?: HTMLVideoElement): void {
    this.audioVideoStreamer.stopPublisherVideo(container);
  }

  setBlur(blurType: CallBlurType): void {
    this.audioVideoStreamer.setBlur(blurType);
    this.call.meStore.setBlur(blurType);
    callBackground.setBlurBackgroundType(blurType);
  }

  async setHandRaised(value: boolean, ts: number | null = null): Promise<boolean> {
    if (!this.audioVideoStreamer.setHandRaised) {
      return false;
    }
    return await this.audioVideoStreamer.setHandRaised(value, ts);
  }

  async getDevices(): Promise<void> {
    await this.changeDeviceService?.getDevices();
  }

  async setCurrentDeviceId(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    await this.changeDeviceService?.setCurrentDeviceId(kind, deviceId);
  }
}
